// src/pages/PhaseOracle.jsx
// Project Oracle — Layer 3: Phase Activity Drilldown
// Full child IDEF0 per phase (A1.x – A5.x)
// Left rail: Project Health Matrix
// Main canvas: React Flow sub-activity diagram
// Right panel: Evidence → AI Analysis → Judgment Flag

import React, { useState, useEffect, useCallback,
                useRef, useMemo } from 'react';
import ReactFlow, {
  Background, Controls,
  useNodesState, useEdgesState,
  Handle, Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────────────────────
// PHASE CHILD DATA — drives everything
// ─────────────────────────────────────────────────────────────

const PHASE_CHILDREN = {
  A1: {
    title: 'Pre-Development',
    period: 'Month 1–6',
    inputs: [
      'Public Gold + Site Data (LBNL Queue, EIA, USGS Flood, BLS Labor)',
      'Utility Queue Docs + Long-Lead Quotes',
      'Initial Pro Forma + Tenant Specs',
    ],
    controls: [
      'Policy Domain 01–04 (Site, Power, Water, Entitlements)',
      'Lender Covenants + AHJ Rules',
    ],
    outputs: [
      'Risk Register + Site Scorecard',
      'Validated Pro Forma + Independent Schedule Baseline',
      'Program Doc + Signed Team Contracts',
      'Permitting Roadmap + Contingency Plan',
    ],
    mechanisms: [
      { label: 'Axiom Reasoning Engine + GPT-4.1', live: true },
      { label: 'Vision Agent (satellite + YOLOv8)', live: false },
      { label: 'Packet Agent (DPI on early OT signals)', live: false },
    ],
    activities: [
      {
        id: 'A1.1',
        label: 'Site Evaluation & Selection',
        sub: 'Geotech · Power Queue · Water · Fiber · Labor · Political',
        corr004Keys: ['queue_delay', 'site_risk'],
        dataLabel: 'LBNL Queue · EIA Grid · NOAA Climate · BLS Labor',
        staticAnalysis: 'Site selection is the highest-leverage decision in DC development. The interconnection queue position determines the hard ceiling on power availability timeline. A site with 70%+ queue withdrawal rate in its ISO region carries an implicit 12–18 month schedule risk that no GC can mitigate. EIA capacity trends reveal whether the grid can physically support the planned load. Labor availability sets the contractor ceiling for execution speed.',
        judgmentTriggers: [
          {
            condition: (s) => s.queue_delay >= 75,
            flag: 'Interconnection queue risk is extreme. Owner must decide: accept 14+ month slip risk with current site, or evaluate alternative sites with lower queue congestion.',
            urgency: 'critical',
            deadline: 'Before land control commitment',
          },
          {
            condition: (s) => s.queue_delay >= 50 && s.queue_delay < 75,
            flag: 'Moderate queue delay risk. Build 6-month contingency into pro forma schedule and engage ISO/utility this week to confirm queue position.',
            urgency: 'warning',
            deadline: 'Within 30 days',
          },
          {
            condition: (s) => s.site_risk >= 60,
            flag: 'Elevated site risk. Commission independent geotechnical and utility feasibility study before committing capital.',
            urgency: 'warning',
            deadline: 'Before LOI execution',
          },
        ],
      },
      {
        id: 'A1.2',
        label: 'Project Feasibility & Pro Forma Challenge',
        sub: 'IRR stress test · Independent schedule baseline · Lender alignment',
        corr004Keys: ['procurement_risk'],
        dataLabel: 'EIA Capacity Trend · Permit Backlog',
        staticAnalysis: 'The owner\'s rep role at feasibility is to challenge the developer\'s pro forma with independent assumptions. Lead-time risk for transformers and switchgear is the most frequently underestimated line item — current procurement windows of 18–52 weeks are not reflected in most developer schedules. Permit backlog data from federal permitting dashboards reveals jurisdiction-specific delay patterns that materially affect the schedule baseline.',
        judgmentTriggers: [
          {
            condition: (s) => s.procurement_risk >= 50,
            flag: 'Procurement risk elevated. Pro forma schedule must include 18–52 week transformer lead time. Owner must approve revised schedule baseline before proceeding.',
            urgency: 'warning',
            deadline: 'Before financial close',
          },
          {
            condition: () => true,
            flag: 'Independent schedule review required. Axiom baseline must be reconciled with GC schedule before lender submission.',
            urgency: 'info',
            deadline: 'Pre-lender package',
          },
        ],
      },
      {
        id: 'A1.3',
        label: 'Program Development + Team Assembly',
        sub: 'Technical program · GC procurement · Architect of record · MEP engineer',
        corr004Keys: [],
        dataLabel: 'Project data required',
        staticAnalysis: 'Program development translates tenant technical specifications into a buildable scope. The owner\'s rep must validate that the program is consistent with the site constraints identified in A1.1 — power capacity, land coverage, cooling strategy. Team assembly at this stage sets the quality ceiling for the entire project. The GC selection process should include a labor market analysis for the specific geography.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must approve technical program and confirm it is consistent with utility capacity confirmation from A1.1 before releasing GC RFP.',
            urgency: 'info',
            deadline: 'Before GC RFP release',
          },
        ],
      },
      {
        id: 'A1.4',
        label: 'Permitting Strategy',
        sub: 'AHJ engagement · Environmental review · Entitlement roadmap',
        corr004Keys: ['procurement_risk'],
        dataLabel: 'Federal Permit Backlog · AHJ Data',
        staticAnalysis: 'Permitting is the most politically variable phase of DC development. Federal environmental review timelines have extended significantly in PJM and WECC territories. Early AHJ engagement — before permit application — is the single highest-ROI activity in this phase. The permitting roadmap must sequence applications to avoid critical-path dependencies that compress the construction window.',
        judgmentTriggers: [
          {
            condition: (s) => s.procurement_risk >= 40,
            flag: 'Permit backlog elevated in this jurisdiction. Owner must decide whether to engage pre-application meeting with AHJ and whether to budget for permit expediting services.',
            urgency: 'warning',
            deadline: 'Month 2',
          },
        ],
      },
    ],
  },

  A2: {
    title: 'Design',
    period: 'Month 3–12',
    inputs: [
      'Program Doc from A1 + Tenant Technical Specs',
      'Long-Lead Vendor Quotes + Public Lead-Time Benchmarks',
    ],
    controls: [
      'Policy Domain 05–08 (Design, VE, Procurement, Coordination)',
      'Hyperscaler SLAs + Engineer-of-Record Liability',
    ],
    outputs: [
      'Approved Construction Documents',
      'VE Savings Report + Risk-Adjusted Design',
      'Early Procurement Authorizations + Schedule Integration',
      'Coordination Model + Open-Issues Log',
    ],
    mechanisms: [
      { label: 'Axiom Reasoning Engine', live: true },
      { label: 'DC Agent (correlation of design signals)', live: true },
    ],
    activities: [
      {
        id: 'A2.1',
        label: 'Design Management + Document Review',
        sub: 'Design schedule · Submittal log · RFI tracking · QA gate reviews',
        corr004Keys: [],
        dataLabel: 'Project data required',
        staticAnalysis: 'Design management at owner-rep level means maintaining an independent document control system that does not rely on the GC or architect. The most common failure mode is undiscovered design conflicts that surface during construction — MEP coordination issues, structural interference with IT infrastructure, or cooling strategy that conflicts with tenant requirements. Gate reviews at 30/60/90% design catch these before they become change orders.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must approve design gate deliverables at 30%, 60%, and 90% completion before authorizing next design phase fees.',
            urgency: 'info',
            deadline: 'Per design schedule',
          },
        ],
      },
      {
        id: 'A2.2',
        label: 'Value Engineering (Cost vs. Technical Integrity)',
        sub: 'Cost reduction · Technical risk assessment · Hyperscaler SLA protection',
        corr004Keys: ['procurement_risk'],
        dataLabel: 'EIA Capacity · Procurement Risk',
        staticAnalysis: 'Value engineering in DC construction is a high-stakes exercise. Unlike commercial real estate, VE cuts that affect power density, cooling redundancy, or connectivity can void hyperscaler acceptance. The owner\'s rep role is to function as the technical referee — approving VE items that reduce cost without affecting SLA compliance, and blocking items that create downstream risk. Every VE decision must be traceable to the technical program.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must personally approve any VE item that affects power redundancy (2N/N+1), cooling capacity, or fiber path diversity. These cannot be delegated.',
            urgency: 'info',
            deadline: 'Per VE workshop schedule',
          },
        ],
      },
      {
        id: 'A2.3',
        label: 'Long-Lead Procurement + Spec Release',
        sub: 'Transformer · Switchgear · Generator · UPS · Cooling units',
        corr004Keys: ['procurement_risk'],
        dataLabel: 'EIA Trend · BLS Labor · Permit Backlog',
        staticAnalysis: 'Long-lead procurement is the schedule-critical path for every data center project. Current transformer lead times range from 18 to 52 weeks depending on manufacturer and specification. The decision to release purchase orders must be made before construction drawings are complete — waiting for 100% design is a guaranteed schedule slip. The owner\'s rep must push the design team to release specifications early enough to capture delivery windows.',
        judgmentTriggers: [
          {
            condition: (s) => s.procurement_risk >= 50,
            flag: 'Lead-time risk elevated. Owner must authorize early procurement spend (typically 15–25% of equipment cost) before construction documents are complete to preserve delivery window.',
            urgency: 'critical',
            deadline: 'Month 6–8',
          },
          {
            condition: (s) => s.procurement_risk >= 25,
            flag: 'Monitor transformer and switchgear lead times weekly. Engage at least 3 manufacturers to maintain competitive optionality.',
            urgency: 'warning',
            deadline: 'Month 4',
          },
        ],
      },
      {
        id: 'A2.4',
        label: 'Interdisciplinary Coordination',
        sub: 'Structural · MEP · IT/low-voltage · Civil · Fire protection',
        corr004Keys: [],
        dataLabel: 'Project data required',
        staticAnalysis: 'Interdisciplinary coordination is where most DC project cost overruns originate. The structural system, MEP routing, IT cabling pathways, and fire protection must be coordinated in 3D before steel is ordered. The owner\'s rep must mandate a clash detection process using BIM coordination — not just 2D overlay. Unresolved clashes that reach the field cost 10–100x more to resolve than on paper.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must confirm BIM coordination process is in contract scope and that a qualified BIM coordinator has been assigned before 60% design.',
            urgency: 'info',
            deadline: 'Month 5',
          },
        ],
      },
    ],
  },

  A3: {
    title: 'Construction',
    period: 'Month 9–24',
    inputs: [
      'Construction Schedule from GC + AISentry Live Signals',
      'Pay Applications + RFIs + Submittals',
    ],
    controls: [
      'Policy Domain 09–12 (Schedule, Cost, Quality, Reporting)',
      'GC Contract + Lender Draw Requirements',
    ],
    outputs: [
      'Independent Schedule Forecast + Delay Warnings',
      'Cost-at-Completion Forecast + Approved Change Orders',
      'Quality Log + Subcontractor Risk Scores',
      'Weekly CEO Briefing + Lender Package',
    ],
    mechanisms: [
      { label: 'Axiom Reasoning Engine', live: true },
      { label: 'Packet + DC Agents (real-time OT + correlation)', live: true },
      { label: 'Vision Agent (site progress)', live: false },
    ],
    activities: [
      {
        id: 'A3.1',
        label: 'Independent Schedule & Critical-Path Management',
        sub: 'GC schedule challenge · Critical path · Float analysis · Delay warnings',
        corr004Keys: ['queue_delay'],
        dataLabel: 'LBNL Queue · ISO Interconnection Position',
        staticAnalysis: 'The GC\'s schedule is a contract document, not an intelligence document. The owner\'s rep maintains an independent schedule that models the project\'s actual critical path — which almost never matches the GC\'s baseline. The interconnection queue position is the external constraint that most frequently overrides the GC\'s assumptions. A queue delay of 6 months that surfaces in Month 18 cannot be recovered by construction acceleration.',
        judgmentTriggers: [
          {
            condition: (s) => s.queue_delay >= 75,
            flag: 'Interconnection slip risk is extreme. Owner must notify lender immediately and initiate discussions with utility about queue position acceleration options.',
            urgency: 'critical',
            deadline: 'This week',
          },
          {
            condition: (s) => s.queue_delay >= 50,
            flag: 'Owner must review GC schedule for assumed energization date and confirm it accounts for current queue delay risk. Adjust lender reporting accordingly.',
            urgency: 'warning',
            deadline: 'Next monthly report',
          },
        ],
      },
      {
        id: 'A3.2',
        label: 'Cost Forecast & Change-Order Evaluation',
        sub: 'Cost-at-completion · Change order review · Contingency management · Lender draws',
        corr004Keys: ['procurement_risk'],
        dataLabel: 'Procurement Risk · EIA Trend',
        staticAnalysis: 'Change order evaluation is the highest-risk activity for an owner\'s rep from a fiduciary standpoint. The GC\'s change order entitlement must be evaluated against the contract, the design documents, and the independent schedule. Procurement-driven change orders — where costs have increased since the bid — require particular scrutiny because the risk allocation depends on exactly when the design was released and when the owner approved early procurement.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must personally approve all change orders above the delegated authority threshold. No change order should be approved without an independent schedule impact analysis.',
            urgency: 'info',
            deadline: 'Per contract terms',
          },
        ],
      },
      {
        id: 'A3.3',
        label: 'Quality & Subcontractor Health Monitoring',
        sub: 'Inspection · Non-conformance · Sub financial health · Workforce levels',
        corr004Keys: ['site_risk'],
        dataLabel: 'BLS Labor · Site Risk',
        staticAnalysis: 'Subcontractor financial distress is a leading indicator of quality problems and schedule slippage. The owner\'s rep monitors pay application timing, workforce headcount trends, and material delivery patterns as proxies for sub health. A subcontractor that is consistently paid late by the GC will reduce workforce before formally defaulting. The labor market score for the site geography determines how quickly a distressed sub can be replaced.',
        judgmentTriggers: [
          {
            condition: (s) => s.site_risk >= 50,
            flag: 'Labor market tightening detected. Owner must confirm GC has executed subcontract agreements with key trades and verify workforce mobilization plan.',
            urgency: 'warning',
            deadline: 'Month 10',
          },
        ],
      },
      {
        id: 'A3.4',
        label: 'Weekly Reporting & Stakeholder Communication',
        sub: 'CEO briefing · Lender package · Investor update · Regulatory reporting',
        corr004Keys: ['queue_delay', 'site_risk', 'procurement_risk'],
        dataLabel: 'Full CORR-004 · AISentry Events',
        staticAnalysis: 'The weekly report is the primary accountability mechanism for the entire project. It must contain an independent assessment — not a summary of what the GC reported. The three-things-needing-judgment framework ensures that decision-makers receive actionable intelligence rather than status reports. Every report should end with explicit owner decisions that have deadlines.',
        judgmentTriggers: [
          {
            condition: (s) =>
              s.queue_delay >= 50 || s.site_risk >= 50 || s.procurement_risk >= 50,
            flag: 'This week\'s report must include a risk escalation section. At least one CORR-004 score is elevated — lender notification may be required per covenant reporting thresholds.',
            urgency: 'warning',
            deadline: 'This Friday',
          },
        ],
      },
    ],
  },

  A4: {
    title: 'Commissioning',
    period: 'Month 18–26',
    inputs: [
      'Cx Agent Plan + AISentry OT Data Streams',
      'Test Scripts + Failure Logs',
    ],
    controls: [
      'Policy Domain 13–15 (Commissioning, Testing, Acceptance)',
      'Hyperscaler Acceptance Criteria + Insurance Requirements',
    ],
    outputs: [
      'Approved Cx Plan + Risk-Gapped Test Sequences',
      'Test Result Dashboard + Failure Remediation Plan',
      'Tenant Sign-Off Package',
      'Commissioning Corpus Update',
    ],
    mechanisms: [
      { label: 'Axiom Reasoning Engine', live: true },
      { label: 'DC Agent (CORR-001/002/003)', live: true },
      { label: 'Packet Agent (protocol-level monitoring)', live: true },
    ],
    activities: [
      {
        id: 'A4.1',
        label: 'Commissioning Plan Review & Gap Analysis',
        sub: 'Cx agent qualifications · Test sequence · Risk-gap identification',
        corr004Keys: ['site_risk'],
        dataLabel: 'AISentry DC Events · Site Risk',
        staticAnalysis: 'The commissioning plan must be reviewed against the tenant\'s acceptance criteria before a single test is conducted. Gap analysis at this stage identifies test sequences that are technically incomplete — typically in integrated systems testing where the interaction between power, cooling, and IT infrastructure is not explicitly tested. These gaps become extremely expensive to address after the tenant\'s commissioning agent has identified them.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must confirm commissioning agent is independent of GC and MEP contractor. Integrated systems test sequence must be approved before MEP completion.',
            urgency: 'info',
            deadline: 'Month 17',
          },
        ],
      },
      {
        id: 'A4.2',
        label: 'Functional Performance Testing Oversight',
        sub: 'HVAC · Electrical · UPS · Generator · BMS integration',
        corr004Keys: ['site_risk'],
        dataLabel: 'AISentry DC Events · OT Protocol Activity',
        staticAnalysis: 'Functional performance testing is where AISentry provides its highest value in the commissioning phase. The DC Agent monitors protocol-level activity across PDU, CRAC, UPS, and BMC during integrated tests — catching anomalies that a visual inspection cannot detect. IPMI authentication failures during generator transfer testing, BACnet write commands from unauthorized sources during HVAC commissioning, and SNMP community string exposure during network integration testing are all detectable at protocol level.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must be present or have a designated representative at all Level 3 and Level 4 integrated systems tests. These cannot be delegated to the GC.',
            urgency: 'info',
            deadline: 'Per Cx schedule',
          },
        ],
      },
      {
        id: 'A4.3',
        label: 'Root-Cause Analysis & Retest Management',
        sub: 'Failure log · RCA process · Retest authorization · Pattern detection',
        corr004Keys: [],
        dataLabel: 'AISentry Correlation Events',
        staticAnalysis: 'Test failures during commissioning are expected and manageable. The risk is not in the failure — it is in inadequate root cause analysis that leads to recurrence. CORR-001/002/003 correlation patterns from AISentry identify whether failures are isolated or systemic. A BMC authentication failure that correlates with physical access events and IPMI anomalies is a security incident, not a commissioning failure. These patterns must be distinguished before retest authorization.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must approve root cause analysis before authorizing retest of any Level 3 or Level 4 failure. RCA must include a correlation check against AISentry event history.',
            urgency: 'info',
            deadline: 'Within 48h of failure',
          },
        ],
      },
      {
        id: 'A4.4',
        label: 'Tenant Acceptance Testing Coordination',
        sub: 'Hyperscaler Cx team · Acceptance protocol · Sign-off package',
        corr004Keys: [],
        dataLabel: 'Project data required',
        staticAnalysis: 'Tenant acceptance testing coordination is the final quality gate before revenue commencement. The hyperscaler\'s commissioning team will conduct their own testing independent of the owner\'s program — the owner\'s rep role is to ensure the facility is ready before the tenant\'s team arrives. Premature presentation to the tenant\'s Cx team is one of the most damaging decisions an owner can make — it signals operational immaturity and can trigger contract remedies.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must certify that all Level 1–4 tests have passed with zero open critical findings before inviting tenant Cx team. Do not rush this decision.',
            urgency: 'critical',
            deadline: 'Before tenant Cx kickoff',
          },
        ],
      },
    ],
  },

  A5: {
    title: 'Turnover & Post-Occupancy',
    period: 'Month 24+',
    inputs: [
      'As-Built Drawings + O&M Manuals + Test Reports',
      'Warranty Claims + Post-Occupancy Alarms (AISentry)',
    ],
    controls: [
      'Policy Domain 16 (Turnover & Operations)',
      'Owner Operations Team Requirements',
    ],
    outputs: [
      'Complete Close-Out Package',
      'Operations Playbook + Trained Team',
      'Warranty Resolution Log + Performance Report',
      'Full Project Corpus Contribution (anonymized)',
    ],
    mechanisms: [
      { label: 'Axiom Reasoning Engine', live: true },
      { label: 'DC + Packet Agents (ongoing OT monitoring)', live: true },
    ],
    activities: [
      {
        id: 'A5.1',
        label: 'Close-Out Documentation Assembly',
        sub: 'As-builts · O&M manuals · Warranties · Attic stock · Spare parts',
        corr004Keys: [],
        dataLabel: 'Project data required',
        staticAnalysis: 'Close-out documentation assembly is consistently underestimated and chronically delayed. The GC\'s contractual obligation to provide as-built drawings and O&M manuals rarely results in usable deliverables without aggressive owner intervention. The owner\'s rep must maintain an independent documentation checklist that tracks completeness — not just receipt. An O&M manual that does not match the as-built condition is worse than no manual at all.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must withhold final retainage until close-out documentation package is complete and verified against as-built conditions. Do not release retainage for incomplete documentation.',
            urgency: 'info',
            deadline: 'Before retainage release',
          },
        ],
      },
      {
        id: 'A5.2',
        label: 'Operations Readiness & Training Support',
        sub: 'O&M team training · Emergency procedures · Runbooks · AISentry handoff',
        corr004Keys: ['site_risk'],
        dataLabel: 'AISentry Baseline Posture · Site Risk',
        staticAnalysis: 'Operations readiness is the bridge between the construction project and the long-term asset. The AISentry handoff is the most important technical event in this phase — the edge agents that monitored the commissioning process become the permanent OT security layer. The operations team must be trained on the AISentry interface, alert response procedures, and the CORR-001/002/003 correlation rules before the construction team demobilizes.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must confirm operations team has completed AISentry training and that emergency response procedures have been tested before construction team final demobilization.',
            urgency: 'info',
            deadline: 'Month 25',
          },
        ],
      },
      {
        id: 'A5.3',
        label: 'Warranty Period Management & Latent Defect Tracking',
        sub: 'Warranty claims · Latent defects · Subcontractor recall · Performance monitoring',
        corr004Keys: [],
        dataLabel: 'AISentry Event History',
        staticAnalysis: 'The warranty period is an underutilized risk management tool. Most owners fail to systematically track latent defects — issues that were present at turnover but only manifest under operational conditions. AISentry\'s continuous OT monitoring provides an unprecedented dataset for warranty claims: protocol anomalies that correlate with equipment defects can be traced back to specific commissioning test failures and specific subcontractors.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner must establish a monthly warranty review process. AISentry anomaly patterns should be reviewed against the commissioning test log for potential warranty claim triggers.',
            urgency: 'info',
            deadline: 'Monthly through warranty period',
          },
        ],
      },
      {
        id: 'A5.4',
        label: 'Lessons-Learned Capture into Corpus',
        sub: 'Anonymized patterns · Schedule actuals · Cost actuals · Risk model update',
        corr004Keys: [],
        dataLabel: 'Full Project Corpus',
        isCorpusNode: true,
        staticAnalysis: 'The lessons-learned contribution to the Axiom corpus is the mechanism by which every completed project makes every future project smarter. Anonymized schedule slippage patterns, cost overrun drivers, procurement delay correlations, and commissioning failure sequences are ingested into the corpus and immediately available for pattern matching against new projects. A new site evaluation in ERCOT benefits from every prior ERCOT project in the corpus.',
        judgmentTriggers: [
          {
            condition: () => true,
            flag: 'Owner to authorize corpus contribution. Anonymized project outcomes improve risk scoring accuracy for all future projects — including owner\'s own future developments.',
            urgency: 'info',
            deadline: 'Within 60 days of substantial completion',
          },
        ],
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────

const scoreToColor = (score) => {
  if (score === null || score === undefined) return {
    bg: '#f8fafc', border: '#cbd5e1', text: '#64748b',
    label: 'Pending', dot: '#94a3b8', pill: '#e2e8f0',
  };
  if (score >= 75) return {
    bg: '#fef2f2', border: '#f87171', text: '#dc2626',
    label: 'High Risk', dot: '#ef4444', pill: '#fee2e2',
  };
  if (score >= 50) return {
    bg: '#fffbeb', border: '#fbbf24', text: '#d97706',
    label: 'Moderate', dot: '#f59e0b', pill: '#fef3c7',
  };
  if (score >= 25) return {
    bg: '#fefce8', border: '#facc15', text: '#ca8a04',
    label: 'Low-Mod', dot: '#eab308', pill: '#fef9c3',
  };
  return {
    bg: '#f0fdf4', border: '#4ade80', text: '#16a34a',
    label: 'Low Risk', dot: '#22c55e', pill: '#dcfce7',
  };
};

const getActivityScore = (activity, siteCorr004) => {
  if (!siteCorr004 || activity.corr004Keys.length === 0) return null;
  const scores = activity.corr004Keys
    .map(k => siteCorr004[k]?.score)
    .filter(s => s !== null && s !== undefined);
  return scores.length ? Math.max(...scores) : null;
};

const getActiveJudgments = (activity, siteCorr004) => {
  const flatScores = {
    queue_delay: siteCorr004?.queue_delay?.score,
    site_risk:   siteCorr004?.site_risk?.score,
    procurement_risk: siteCorr004?.procurement_risk?.score,
  };
  return activity.judgmentTriggers.filter(t => t.condition(flatScores));
};

// ─────────────────────────────────────────────────────────────
// REACT FLOW NODE TYPES
// ─────────────────────────────────────────────────────────────

const ActivityNode = ({ data }) => {
  const colors = scoreToColor(data.score);
  return (
    <div
      onClick={data.onClick}
      style={{
        background: colors.bg,
        border: `2px solid ${data.selected ? '#1e40af' : colors.border}`,
        borderRadius: 12,
        padding: '12px 16px',
        minWidth: 200,
        maxWidth: 240,
        cursor: 'pointer',
        boxShadow: data.selected
          ? '0 0 0 3px rgba(30,64,175,0.3)'
          : colors.dot !== '#94a3b8'
          ? '0 0 16px rgba(0,0,0,0.08)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.zIndex = '10';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.zIndex = '1';
      }}
    >
      <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      {/* ID badge */}
      <div style={{
        position: 'absolute', top: -10, left: 12,
        background: colors.dot, color: '#fff',
        fontSize: 9, fontWeight: 800, padding: '2px 8px',
        borderRadius: 999, fontFamily: 'monospace',
        letterSpacing: 1,
      }}>
        {data.id}
      </div>

      {/* Judgment flag dot */}
      {data.judgmentCount > 0 && (
        <div style={{
          position: 'absolute', top: -8, right: 8,
          background: '#ef4444', color: '#fff',
          fontSize: 8, fontWeight: 900, padding: '1px 5px',
          borderRadius: 999, animation: 'pulse 2s infinite',
        }}>
          {data.judgmentCount} flag{data.judgmentCount > 1 ? 's' : ''}
        </div>
      )}

      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#0f172a',
                      lineHeight: 1.3, marginBottom: 4 }}>
          {data.label}
        </div>
        <div style={{ fontSize: 9, color: '#64748b', lineHeight: 1.4,
                      marginBottom: 8 }}>
          {data.sub}
        </div>

        {/* Score pill */}
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: colors.pill, borderRadius: 999,
            padding: '2px 8px',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%',
                          background: colors.dot }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: colors.text }}>
              {data.score !== null
                ? `${Math.round(data.score)}/100 \u00B7 ${colors.label}`
                : colors.label}
            </span>
          </div>
          <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>
            Click \u2192
          </span>
        </div>

        {/* Data source label */}
        {data.dataLabel && data.score !== null && (
          <div style={{ fontSize: 8, color: '#94a3b8', marginTop: 5,
                        fontStyle: 'italic' }}>
            {data.dataLabel}
          </div>
        )}
      </div>
    </div>
  );
};

const IONode = ({ data }) => (
  <div style={{
    background: data.type === 'control'   ? '#f0f9ff'
              : data.type === 'mechanism' ? '#faf5ff'
              : data.type === 'output'    ? '#f0fdf4'
              : '#f8fafc',
    border: `1px solid ${
      data.type === 'control'   ? '#bae6fd'
      : data.type === 'mechanism' ? '#e9d5ff'
      : data.type === 'output'    ? '#bbf7d0'
      : '#e2e8f0'
    }`,
    borderRadius: 8, padding: '5px 10px',
    fontSize: 9, fontWeight: 600, maxWidth: 160,
    textAlign: 'center', lineHeight: 1.4,
    color: data.type === 'control'   ? '#0369a1'
         : data.type === 'mechanism' ? '#7c3aed'
         : data.type === 'output'    ? '#15803d'
         : '#475569',
    display: 'flex', alignItems: 'center', gap: 4,
  }}>
    <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    <Handle type="target" position={Position.Left}  style={{ opacity: 0 }} />
    {data.live && (
      <div style={{ width: 5, height: 5, borderRadius: '50%',
                    background: '#22c55e', flexShrink: 0,
                    animation: 'pulse 2s infinite' }} />
    )}
    {data.label}
  </div>
);

const phaseNodeTypes = { activityNode: ActivityNode, ioNode: IONode };

// ─────────────────────────────────────────────────────────────
// BUILD PHASE FLOW
// ─────────────────────────────────────────────────────────────

const buildPhaseFlow = (phaseId, siteCorr004, selectedActivityId,
                        onActivityClick) => {
  const phase = PHASE_CHILDREN[phaseId];
  if (!phase) return { nodes: [], edges: [] };

  const nodes = [];
  const edges = [];

  // Layout constants
  const ACTIVITY_X    = 320;
  const ACTIVITY_Y_START = 160;
  const ACTIVITY_GAP  = 200;
  const INPUT_X       = 20;
  const OUTPUT_X      = 620;
  const TOTAL_HEIGHT  = ACTIVITY_Y_START
                        + (phase.activities.length - 1) * ACTIVITY_GAP;

  // Activity nodes
  phase.activities.forEach((act, idx) => {
    const score = getActivityScore(act, siteCorr004);
    const judgments = getActiveJudgments(act, siteCorr004);
    const y = ACTIVITY_Y_START + idx * ACTIVITY_GAP;
    nodes.push({
      id: act.id,
      type: 'activityNode',
      position: { x: ACTIVITY_X, y },
      data: {
        ...act,
        score,
        judgmentCount: judgments.length,
        selected: selectedActivityId === act.id,
        onClick: () => onActivityClick(act),
      },
    });
  });

  // Input nodes
  phase.inputs.forEach((inp, idx) => {
    const id = `input-${idx}`;
    nodes.push({
      id,
      type: 'ioNode',
      position: { x: INPUT_X, y: ACTIVITY_Y_START + idx * 110 },
      data: { label: inp, type: 'input', live: idx === 0 },
    });
    // Connect to first activity
    edges.push({
      id: `${id}-act`,
      source: id, target: phase.activities[0].id,
      style: { stroke: '#94a3b8', strokeWidth: 1 },
    });
  });

  // Output nodes
  phase.outputs.forEach((out, idx) => {
    const id = `output-${idx}`;
    nodes.push({
      id,
      type: 'ioNode',
      position: { x: OUTPUT_X, y: ACTIVITY_Y_START + idx * 110 },
      data: { label: out, type: 'output' },
    });
    // Connect from last activity
    edges.push({
      id: `act-${id}`,
      source: phase.activities[phase.activities.length - 1].id,
      target: id,
      style: { stroke: '#16a34a', strokeWidth: 1 },
    });
  });

  // Controls fanned above — centred over activity column
  const totalControls = phase.controls.length;
  const ctrlSpread = 220;
  const ctrlStartX = ACTIVITY_X + 100
    - ((totalControls - 1) * ctrlSpread) / 2;
  phase.controls.forEach((ctrl, idx) => {
    const id = `control-${idx}`;
    nodes.push({
      id,
      type: 'ioNode',
      position: {
        x: ctrlStartX + idx * ctrlSpread,
        y: 20,
      },
      data: { label: ctrl, type: 'control' },
    });
    // Dashed edge down to first activity
    edges.push({
      id: `ctrl-${idx}-act`,
      source: id,
      target: phase.activities[0].id,
      style: { stroke: '#bae6fd', strokeWidth: 1,
               strokeDasharray: '4 3' },
    });
  });

  // Mechanisms fanned below — centred under activity column
  const totalMechs = phase.mechanisms.length;
  const mechSpread = 220;
  const mechStartX = ACTIVITY_X + 100
    - ((totalMechs - 1) * mechSpread) / 2;
  const MECH_Y = TOTAL_HEIGHT + 160;
  phase.mechanisms.forEach((mech, idx) => {
    const id = `mech-${idx}`;
    nodes.push({
      id,
      type: 'ioNode',
      position: {
        x: mechStartX + idx * mechSpread,
        y: MECH_Y,
      },
      data: { label: mech.label, type: 'mechanism', live: mech.live },
    });
    // Dashed edge up to last activity
    edges.push({
      id: `mech-${idx}-act`,
      source: id,
      target: phase.activities[phase.activities.length - 1].id,
      style: { stroke: '#e9d5ff', strokeWidth: 1,
               strokeDasharray: '4 3' },
    });
  });

  // Activity flow edges
  for (let i = 0; i < phase.activities.length - 1; i++) {
    edges.push({
      id: `flow-${i}`,
      source: phase.activities[i].id,
      target: phase.activities[i + 1].id,
      animated: true,
      style: { stroke: '#6366f1', strokeWidth: 1.5,
               strokeDasharray: '4 2' },
    });
  }

  return { nodes, edges };
};

// ─────────────────────────────────────────────────────────────
// PROJECT HEALTH MATRIX (Left Rail)
// ─────────────────────────────────────────────────────────────

const HealthMatrix = ({ allPhases, siteCorr004,
                        activePhase, onPhaseClick, onActivityClick }) => (
  <div style={{
    position: 'absolute', left: 0, top: 0, bottom: 0,
    width: 200, background: '#fff',
    borderRight: '1px solid #e2e8f0',
    zIndex: 20, overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  }}>
    {/* Header */}
    <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280',
                    textTransform: 'uppercase', letterSpacing: 1 }}>
        Project Health Matrix
      </div>
      <div style={{ fontSize: 8, color: '#9ca3af', marginTop: 2 }}>
        Click any cell to navigate
      </div>
    </div>

    {/* Phase rows */}
    {allPhases.map(([phaseId, phase]) => {
      const isActive = activePhase === phaseId;
      return (
        <div key={phaseId} style={{
          borderBottom: '1px solid #f1f5f9',
          background: isActive ? '#eff6ff' : '#fff',
        }}>
          {/* Phase label */}
          <div
            onClick={() => onPhaseClick(phaseId)}
            style={{
              padding: '8px 14px 4px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{
              fontSize: 9, fontWeight: 800, fontFamily: 'monospace',
              color: isActive ? '#1e40af' : '#374151',
              background: isActive ? '#dbeafe' : '#f1f5f9',
              padding: '1px 5px', borderRadius: 4,
            }}>{phaseId}</span>
            <span style={{ fontSize: 9, fontWeight: 600,
                           color: isActive ? '#1e40af' : '#374151',
                           flex: 1, overflow: 'hidden',
                           textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {phase.title}
            </span>
          </div>

          {/* Activity cells — 2x2 grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: 3, padding: '0 10px 8px' }}>
            {phase.activities.map(act => {
              const score = getActivityScore(act, siteCorr004);
              const colors = scoreToColor(score);
              const judgments = getActiveJudgments(act, siteCorr004);
              return (
                <div
                  key={act.id}
                  onClick={() => onActivityClick(phaseId, act)}
                  title={act.label}
                  style={{
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6, padding: '5px 6px',
                    cursor: 'pointer', position: 'relative',
                    transition: 'transform 0.1s',
                  }}
                  onMouseEnter={e =>
                    e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={e =>
                    e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div style={{ fontSize: 8, fontWeight: 800,
                                color: colors.text, fontFamily: 'monospace' }}>
                    {act.id}
                  </div>
                  <div style={{ fontSize: 7, color: '#64748b',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', marginTop: 1 }}>
                    {act.label.split(' ')[0]}
                  </div>
                  {score !== null && (
                    <div style={{ fontSize: 9, fontWeight: 900,
                                  color: colors.text, marginTop: 1 }}>
                      {Math.round(score)}
                    </div>
                  )}
                  {judgments.length > 0 && (
                    <div style={{
                      position: 'absolute', top: 2, right: 2,
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#ef4444',
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    })}

    {/* Legend */}
    <div style={{ marginTop: 'auto', padding: '10px 14px',
                  borderTop: '1px solid #f1f5f9' }}>
      {[
        { color: '#ef4444', label: 'High Risk (75+)' },
        { color: '#f59e0b', label: 'Moderate (50\u201374)' },
        { color: '#eab308', label: 'Low-Mod (25\u201349)' },
        { color: '#22c55e', label: 'Low Risk (<25)' },
        { color: '#94a3b8', label: 'Pending data' },
      ].map(l => (
        <div key={l.label} style={{ display: 'flex', alignItems: 'center',
                                    gap: 6, marginBottom: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2,
                        background: l.color, flexShrink: 0 }} />
          <span style={{ fontSize: 8, color: '#6b7280' }}>{l.label}</span>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center',
                    gap: 6, marginTop: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%',
                      background: '#ef4444', flexShrink: 0 }} />
        <span style={{ fontSize: 8, color: '#6b7280' }}>Judgment flag</span>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ACTIVITY PANEL (Right — Evidence + AI + Judgment)
// ─────────────────────────────────────────────────────────────

const ActivityPanel = ({ activity, siteCorr004, siteName,
                         phaseId, onClose }) => {
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const abortRef = useRef(null);

  const score = getActivityScore(activity, siteCorr004);
  const colors = scoreToColor(score);
  const judgments = getActiveJudgments(activity, siteCorr004);

  // Stream AI analysis on mount
  useEffect(() => {
    if (!activity) return;
    setAiText('');
    setAiDone(false);
    setAiLoading(true);

    const scores = {
      queue_delay: siteCorr004?.queue_delay?.score,
      site_risk: siteCorr004?.site_risk?.score,
      procurement_risk: siteCorr004?.procurement_risk?.score,
    };

    const prompt = `You are Axiom, the AI reasoning engine for DC infrastructure projects.

Site: ${siteName}
Phase: ${phaseId} \u2014 ${PHASE_CHILDREN[phaseId]?.title}
Activity: ${activity.id} \u2014 ${activity.label}

CORR-004 Scores:
- Queue Delay Risk: ${scores.queue_delay !== null && scores.queue_delay !== undefined ? Math.round(scores.queue_delay) + '/100' : 'No data'}
- Site Risk: ${scores.site_risk !== null && scores.site_risk !== undefined ? Math.round(scores.site_risk) + '/100' : 'No data'}
- Procurement Risk: ${scores.procurement_risk !== null && scores.procurement_risk !== undefined ? Math.round(scores.procurement_risk) + '/100' : 'No data'}

Provide a 3-sentence site-specific analysis of this activity given the CORR-004 scores above.
Be direct, specific to this site and these scores, and actionable.
Do not repeat the scores back \u2014 interpret them in context of this activity.
End with one specific action the owner should take this week.`;

    const controller = new AbortController();
    abortRef.current = controller;

    fetch(`${API_BASE}/chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message: prompt }),
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(data => {
        setAiText(data.content || data.message || '');
        setAiDone(true);
      })
      .catch(e => {
        if (e.name !== 'AbortError') {
          setAiText(activity.staticAnalysis);
          setAiDone(true);
        }
      })
      .finally(() => setAiLoading(false));

    return () => controller.abort();
  }, [activity, siteName, phaseId, siteCorr004]);

  if (!activity) return null;

  return (
    <div style={{
      position: 'absolute', right: 0, top: 0, bottom: 0,
      width: 360, background: '#fff',
      borderLeft: '1px solid #e2e8f0',
      boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
      zIndex: 30, overflowY: 'auto',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 18px',
        background: colors.bg,
        borderBottom: `2px solid ${colors.border}`,
        position: 'sticky', top: 0, zIndex: 1,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                          marginBottom: 4 }}>
              <span style={{
                background: colors.dot, color: '#fff',
                fontSize: 9, fontWeight: 800, padding: '2px 8px',
                borderRadius: 999, fontFamily: 'monospace',
              }}>{activity.id}</span>
              <span style={{ fontSize: 10, color: colors.text,
                             fontWeight: 700 }}>
                {colors.label}
                {score !== null && ` \u00B7 ${Math.round(score)}/100`}
              </span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a',
                          lineHeight: 1.3 }}>
              {activity.label}
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>
              {activity.sub}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: '#f1f5f9', border: 'none', borderRadius: 8,
            padding: '6px 10px', cursor: 'pointer', fontSize: 14,
            color: '#374151', flexShrink: 0, marginLeft: 8,
          }}>{'\u2715'}</button>
        </div>
      </div>

      <div style={{ flex: 1, padding: '16px 18px',
                    display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* SECTION 1: EVIDENCE */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#374151',
                        textTransform: 'uppercase', letterSpacing: 1,
                        marginBottom: 10, display: 'flex',
                        alignItems: 'center', gap: 6 }}>
            <div style={{ width: 3, height: 12, background: '#1e40af',
                          borderRadius: 2 }} />
            Evidence — CORR-004 Intelligence
          </div>

          {activity.corr004Keys.length === 0 ? (
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 10, padding: 12, fontSize: 11,
              color: '#94a3b8', fontStyle: 'italic', textAlign: 'center',
            }}>
              No public dataset correlators for this activity.
              <br />
              <span style={{ fontSize: 10 }}>
                Connect project data to activate.
              </span>
            </div>
          ) : activity.corr004Keys.map(key => {
            const row = siteCorr004?.[key];
            const c = scoreToColor(row?.score ?? null);
            return (
              <div key={key} style={{
                background: c.bg, border: `1px solid ${c.border}`,
                borderRadius: 10, padding: '10px 12px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700,
                                 color: '#374151', textTransform: 'uppercase',
                                 letterSpacing: 0.5 }}>
                    {key.replace(/_/g, ' ')}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 900,
                                   color: c.text }}>
                      {row ? `${Math.round(row.score)}/100` : '\u2014'}
                    </span>
                    {row && (
                      <span style={{ fontSize: 9, color: c.text,
                                     background: c.pill, padding: '1px 6px',
                                     borderRadius: 999, fontWeight: 600 }}>
                        {row.confidence}
                      </span>
                    )}
                  </div>
                </div>
                {row?.summary && (
                  <div style={{ fontSize: 10, color: '#475569',
                                lineHeight: 1.6, marginBottom: 6 }}>
                    {row.summary}
                  </div>
                )}
                {row?.evidence && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(row.evidence)
                      .filter(([k, v]) => v !== null && v !== undefined
                               && !['geo_state','iso_region'].includes(k))
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <div key={k} style={{
                          background: '#fff', border: `1px solid ${c.border}`,
                          borderRadius: 6, padding: '2px 7px',
                          fontSize: 9, color: '#374151',
                        }}>
                          <span style={{ color: '#9ca3af' }}>
                            {k.replace(/_/g, ' ')}:
                          </span>{' '}
                          <span style={{ fontWeight: 700 }}>
                            {typeof v === 'number' ? v.toLocaleString() : v}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Corpus counter for A5.4 */}
          {activity.isCorpusNode && (
            <div style={{
              background: 'linear-gradient(135deg, #1e3a8a, #1e40af)',
              borderRadius: 10, padding: '12px 14px', marginTop: 8,
            }}>
              <div style={{ fontSize: 9, color: '#93c5fd', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: 1,
                            marginBottom: 4 }}>
                Axiom Corpus
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>
                38,000+
              </div>
              <div style={{ fontSize: 10, color: '#bfdbfe', marginTop: 2 }}>
                LBNL queue records in corpus
              </div>
              <div style={{ fontSize: 10, color: '#93c5fd', marginTop: 6,
                            lineHeight: 1.5 }}>
                Every anonymized project outcome contributed here improves
                risk scoring accuracy for all future Aligned builds.
              </div>
            </div>
          )}
        </div>

        {/* SECTION 2: AI ANALYSIS */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#374151',
                        textTransform: 'uppercase', letterSpacing: 1,
                        marginBottom: 10, display: 'flex',
                        alignItems: 'center', gap: 6 }}>
            <div style={{ width: 3, height: 12, background: '#7c3aed',
                          borderRadius: 2 }} />
            Axiom Analysis
            {aiLoading && (
              <div style={{ width: 8, height: 8, borderRadius: '50%',
                            background: '#7c3aed',
                            animation: 'pulse 1s infinite', marginLeft: 4 }} />
            )}
          </div>
          <div style={{
            background: '#faf5ff', border: '1px solid #e9d5ff',
            borderRadius: 10, padding: '12px 14px',
            fontSize: 11, color: '#374151', lineHeight: 1.7,
            minHeight: 80,
          }}>
            {aiLoading && !aiText && (
              <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                Axiom is analyzing {activity.id} for {siteName}...
              </div>
            )}
            {aiText || (!aiLoading && activity.staticAnalysis)}
            {aiLoading && aiText && (
              <span style={{ display: 'inline-block', width: 8, height: 12,
                             background: '#7c3aed', marginLeft: 2,
                             animation: 'pulse 1s infinite',
                             verticalAlign: 'middle' }} />
            )}
          </div>
        </div>

        {/* SECTION 3: JUDGMENT FLAGS */}
        {judgments.length > 0 && (
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#374151',
                          textTransform: 'uppercase', letterSpacing: 1,
                          marginBottom: 10, display: 'flex',
                          alignItems: 'center', gap: 6 }}>
              <div style={{ width: 3, height: 12, background: '#ef4444',
                            borderRadius: 2 }} />
              Owner Judgment Required
            </div>
            {judgments.map((j, idx) => (
              <div key={idx} style={{
                background: j.urgency === 'critical' ? '#fef2f2'
                          : j.urgency === 'warning'  ? '#fffbeb'
                          : '#f0f9ff',
                border: `1px solid ${
                  j.urgency === 'critical' ? '#fca5a5'
                  : j.urgency === 'warning'  ? '#fcd34d'
                  : '#bae6fd'
                }`,
                borderLeft: `4px solid ${
                  j.urgency === 'critical' ? '#ef4444'
                  : j.urgency === 'warning'  ? '#f59e0b'
                  : '#0ea5e9'
                }`,
                borderRadius: '0 10px 10px 0',
                padding: '10px 12px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 800,
                                 textTransform: 'uppercase', letterSpacing: 1,
                                 color: j.urgency === 'critical' ? '#dc2626'
                                      : j.urgency === 'warning'  ? '#d97706'
                                      : '#0369a1' }}>
                    {j.urgency === 'critical' ? 'Critical Decision'
                     : j.urgency === 'warning' ? 'Action Required'
                     : 'Owner Note'}
                  </span>
                  {j.deadline && (
                    <span style={{ fontSize: 8, color: '#6b7280',
                                   background: '#f1f5f9', padding: '1px 6px',
                                   borderRadius: 999, fontWeight: 600 }}>
                      {j.deadline}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#1f2937',
                              lineHeight: 1.6 }}>
                  {j.flag}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No judgments — positive signal */}
        {judgments.length === 0 && score !== null && score < 50 && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderLeft: '4px solid #22c55e',
            borderRadius: '0 10px 10px 0', padding: '10px 12px',
          }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#15803d',
                          textTransform: 'uppercase', letterSpacing: 1,
                          marginBottom: 4 }}>
              No Flags
            </div>
            <div style={{ fontSize: 11, color: '#166534', lineHeight: 1.6 }}>
              CORR-004 scores within acceptable range for this activity.
              Continue per plan.
            </div>
          </div>
        )}
      </div>

      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PhaseOracle COMPONENT
// ─────────────────────────────────────────────────────────────

const PhaseOracle = ({
  site,
  initialPhaseId,
  siteCorr004,
  onBack,
}) => {
  const [activePhaseId, setActivePhaseId] = useState(
    initialPhaseId || 'A1'
  );
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);

  const allPhases = Object.entries(PHASE_CHILDREN);

  // Build flow when phase changes
  useEffect(() => {
    const { nodes, edges } = buildPhaseFlow(
      activePhaseId,
      siteCorr004,
      selectedActivity?.id,
      (act) => setSelectedActivity(act),
    );
    setFlowNodes(nodes);
    setFlowEdges(edges);
  }, [activePhaseId, siteCorr004, selectedActivity?.id,
      setFlowNodes, setFlowEdges]);

  const handleMatrixActivityClick = (phaseId, act) => {
    setActivePhaseId(phaseId);
    setSelectedActivity(act);
  };

  const activePhase = PHASE_CHILDREN[activePhaseId];

  return (
    <div style={{
      height: 'calc(100vh - 64px)', position: 'relative',
      overflow: 'hidden', background: '#f8fafc',
    }}>

      {/* Breadcrumb header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 52,
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        zIndex: 40, display: 'flex', alignItems: 'center',
        padding: '0 16px', gap: 8,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#6b7280', fontSize: 12, display: 'flex',
          alignItems: 'center', gap: 4, padding: '4px 8px',
          borderRadius: 6, fontWeight: 600,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          {'\u2190'} Portfolio
        </button>
        <span style={{ color: '#d1d5db' }}>{'\u203A'}</span>
        <button onClick={() => onBack('site')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#6b7280', fontSize: 12, padding: '4px 8px',
          borderRadius: 6, fontWeight: 600,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          {site?.name}
        </button>
        <span style={{ color: '#d1d5db' }}>{'\u203A'}</span>
        <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 700 }}>
          {activePhaseId}: {activePhase?.title}
        </span>
        <span style={{ color: '#d1d5db' }}>{'\u00B7'}</span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          {activePhase?.period}
        </span>

        {/* Phase tabs */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {allPhases.map(([phaseId, phase]) => {
            const phaseScores = phase.activities.map(act =>
              getActivityScore(act, siteCorr004)
            ).filter(s => s !== null);
            const maxScore = phaseScores.length
              ? Math.max(...phaseScores) : null;
            const c = scoreToColor(maxScore);
            const isActive = phaseId === activePhaseId;
            return (
              <button
                key={phaseId}
                onClick={() => { setActivePhaseId(phaseId);
                                 setSelectedActivity(null); }}
                style={{
                  background: isActive ? '#eff6ff' : '#fff',
                  border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`,
                  borderRadius: 8, padding: '4px 10px',
                  cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  color: isActive ? '#1e40af' : '#374151',
                  display: 'flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ width: 6, height: 6, borderRadius: '50%',
                              background: c.dot }} />
                {phaseId}
              </button>
            );
          })}
        </div>
      </div>

      {/* Left rail — Health Matrix */}
      <div style={{ position: 'absolute', top: 52, left: 0, bottom: 0,
                    width: 200, zIndex: 20 }}>
        <HealthMatrix
          allPhases={allPhases}
          siteCorr004={siteCorr004}
          activePhase={activePhaseId}
          onPhaseClick={(id) => { setActivePhaseId(id);
                                  setSelectedActivity(null); }}
          onActivityClick={handleMatrixActivityClick}
        />
      </div>

      {/* React Flow canvas */}
      <div style={{
        position: 'absolute', top: 52, left: 200,
        right: selectedActivity ? 360 : 0, bottom: 0,
      }}>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={phaseNodeTypes}
          fitView
          fitViewOptions={{ padding: 0.25 }}
          minZoom={0.2}
          maxZoom={1.8}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1}
                      color="rgba(0,0,0,0.04)" />
          <Controls className="!bg-white !border-slate-200
                               !rounded-xl !shadow-lg" />
        </ReactFlow>
      </div>

      {/* Right panel */}
      {selectedActivity && (
        <div style={{ position: 'absolute', top: 52, right: 0,
                      bottom: 0, width: 360, zIndex: 30 }}>
          <ActivityPanel
            activity={selectedActivity}
            siteCorr004={siteCorr004}
            siteName={site?.name}
            phaseId={activePhaseId}
            onClose={() => setSelectedActivity(null)}
          />
        </div>
      )}
    </div>
  );
};

export default PhaseOracle;
export { PHASE_CHILDREN };
